import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createNoteReport,
  createPersonalNote,
  deleteNoteReport,
  deletePersonalNote,
  fetchNoteReports,
  fetchNoteViewActivity,
  fetchPersonalNotes,
  toggleNoteBookmark,
  toggleNoteLike,
} from "../services/notesFeedbackService";

function normalizeReport(item = {}, index = 0) {
  return {
    id: item?.report_id || item?.id || index + 1,
    description: item?.description || "",
    replyDescription: item?.reply_description || "",
  };
}

function normalizeNote(item = {}, index = 0) {
  return {
    id: item?.note_id || item?.id || index + 1,
    description: item?.description || "",
  };
}

function normalizeViewFeedItem(item = {}, index = 0) {
  const type = String(item?.type || "").toLowerCase();
  const normalizedType =
    type === "like"
      ? "Like"
      : type === "view"
        ? "View"
        : type === "report"
          ? "Report"
          : type === "note" || type === "notes"
            ? "Notes"
            : "All";

  return {
    id: item?.user_id || item?.id || index + 1,
    name: [item?.title, item?.name || item?.user_name].filter(Boolean).join(" ") || "User",
    avatar: item?.profile_pics || item?.profile_pic || item?.avatar || "",
    time: item?.updated_at || item?.created_at || "",
    type: normalizedType,
    text:
      item?.description ||
      (normalizedType === "Like"
        ? "Liked this note"
        : normalizedType === "View"
          ? "Viewed this note"
          : normalizedType === "Report"
            ? "Reported an issue"
            : normalizedType === "Notes"
              ? "Added a note"
              : "Activity"),
  };
}

function updateNotesInCache(currentData, noteId, updater) {
  if (!currentData?.notes?.length) {
    return currentData;
  }

  return {
    ...currentData,
    notes: currentData.notes.map((note) =>
      Number(note?.id) === Number(noteId) ? updater(note) : note
    ),
  };
}

function toSafeReportCount(value) {
  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) ? normalizedValue : 0;
}

export function useNoteReports(noteId, options = {}) {
  return useQuery({
    queryKey: ["noteReports", Number(noteId)],
    queryFn: async () => {
      const { payload } = await fetchNoteReports({
        notes_id: Number(noteId),
      });

      return {
        totalReportsLabel: payload?.total_reports || "0",
        isBold: Number(payload?.is_bold || 0) === 1,
        items: (Array.isArray(payload?.user_reports) ? payload.user_reports : []).map(normalizeReport),
      };
    },
    enabled: Boolean(noteId) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: {
      totalReportsLabel: "0",
      isBold: false,
      items: [],
    },
  });
}

export function useNotePersonalNotes(noteId, options = {}) {
  return useQuery({
    queryKey: ["notePersonalNotes", Number(noteId)],
    queryFn: async () => {
      const { payload } = await fetchPersonalNotes({
        notes_id: Number(noteId),
      });

      return {
        totalNotes: Number(payload?.total_notes || 0),
        items: (Array.isArray(payload?.user_notes) ? payload.user_notes : []).map(normalizeNote),
      };
    },
    enabled: Boolean(noteId) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: {
      totalNotes: 0,
      items: [],
    },
  });
}

export function useNoteViewActivity(noteId, options = {}) {
  return useQuery({
    queryKey: ["noteViewActivity", Number(noteId)],
    queryFn: async () => {
      const { payload } = await fetchNoteViewActivity({
        notes_id: Number(noteId),
      });

      const allItems = Array.isArray(payload?.all) ? payload.all.map(normalizeViewFeedItem) : [];
      const likeItems = Array.isArray(payload?.likes)
        ? payload.likes.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "Like");
      const viewItems = Array.isArray(payload?.views)
        ? payload.views.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "View");
      const reportItems = Array.isArray(payload?.reports)
        ? payload.reports.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "Report");
      const notesItems = Array.isArray(payload?.notes)
        ? payload.notes.map(normalizeViewFeedItem)
        : allItems.filter((item) => item.type === "Notes");

      return {
        totalData: Number(payload?.total_data || payload?.all?.length || allItems.length || 0),
        totalLikes: Number(payload?.total_likes || likeItems.length || 0),
        totalViews: Number(payload?.total_views || viewItems.length || 0),
        totalNotes: Number(payload?.total_notes || notesItems.length || 0),
        totalReports: Number(payload?.total_reports || reportItems.length || 0),
        all: allItems,
        like: likeItems,
        view: viewItems,
        report: reportItems,
        notes: notesItems,
      };
    },
    enabled: Boolean(noteId) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: {
      totalData: 0,
      totalLikes: 0,
      totalViews: 0,
      totalNotes: 0,
      totalReports: 0,
      all: [],
      like: [],
      view: [],
      report: [],
      notes: [],
    },
  });
}

export function useNotesListActions(notesQueryParams = {}) {
  const queryClient = useQueryClient();
  const queryKey = ["notesList", notesQueryParams];

  const bookmarkMutation = useMutation({
    mutationFn: toggleNoteBookmark,
    onMutate: async (variables) => {
      const noteId = Number(variables?.notes_id);
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => ({
          ...note,
          isBookmarked: !note.isBookmarked,
        }))
      );

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: ({ payload }, variables) => {
      const noteId = Number(variables?.notes_id);
      const nextStatus = Number(payload?.bookmark_status ?? 0) === 1;

      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => ({
          ...note,
          isBookmarked: nextStatus,
        }))
      );
    },
  });

  const likeMutation = useMutation({
    mutationFn: toggleNoteLike,
    onMutate: async (variables) => {
      const noteId = Number(variables?.notes_id);
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => {
          const nextLikedState = !note.isLiked;
          return {
            ...note,
            isLiked: nextLikedState,
            likeCount: Math.max(0, Number(note.likeCount || 0) + (nextLikedState ? 1 : -1)),
          };
        })
      );

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: ({ payload }, variables) => {
      const noteId = Number(variables?.notes_id);
      const nextStatus = Number(payload?.like_status ?? 0) === 1;
      const totalLikes = Number(payload?.total_likes ?? 0);

      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => ({
          ...note,
          isLiked: nextStatus,
          likeCount: totalLikes,
        }))
      );
    },
  });

  const createReport = useMutation({
    mutationFn: createNoteReport,
    onSuccess: (_result, variables) => {
      const noteId = Number(variables?.notes_id);

      queryClient.invalidateQueries({ queryKey: ["noteReports", noteId] });
      queryClient.invalidateQueries({ queryKey: ["noteViewActivity", noteId] });
      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => ({
          ...note,
          reportCount: String(toSafeReportCount(note.reportCount) + 1),
          reportStatus: true,
        }))
      );
    },
  });

  const deleteReport = useMutation({
    mutationFn: deleteNoteReport,
    onSuccess: (_result, variables) => {
      const noteId = Number(variables?.notes_id);

      queryClient.invalidateQueries({ queryKey: ["noteReports", noteId] });
      queryClient.invalidateQueries({ queryKey: ["noteViewActivity", noteId] });
      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => {
          const nextCount = Math.max(0, toSafeReportCount(note.reportCount) - 1);
          return {
            ...note,
            reportCount: String(nextCount),
            reportStatus: nextCount > 0,
          };
        })
      );
    },
  });

  const createNote = useMutation({
    mutationFn: createPersonalNote,
    onSuccess: (_result, variables) => {
      const noteId = Number(variables?.notes_id);

      queryClient.invalidateQueries({ queryKey: ["notePersonalNotes", noteId] });
      queryClient.invalidateQueries({ queryKey: ["noteViewActivity", noteId] });
      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => ({
          ...note,
          notesCount: Number(note.notesCount || 0) + 1,
        }))
      );
    },
  });

  const deleteNote = useMutation({
    mutationFn: deletePersonalNote,
    onSuccess: (_result, variables) => {
      const noteId = Number(variables?.notes_id);

      queryClient.invalidateQueries({ queryKey: ["notePersonalNotes", noteId] });
      queryClient.invalidateQueries({ queryKey: ["noteViewActivity", noteId] });
      queryClient.setQueryData(queryKey, (currentData) =>
        updateNotesInCache(currentData, noteId, (note) => ({
          ...note,
          notesCount: Math.max(0, Number(note.notesCount || 0) - 1),
        }))
      );
    },
  });

  return {
    toggleBookmark: bookmarkMutation,
    toggleLike: likeMutation,
    createReport,
    deleteReport,
    createNote,
    deleteNote,
  };
}

